import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Loader, Progress, Text } from "@mbank-design/design-system/components";
import { ProgressColor } from "@mbank-design/design-system/components/Progress/Progress.constants";
import { LoaderSize } from "@mbank-design/design-system/enums";
import { QRCodeSVG } from "qrcode.react";
import { Context } from "../../context";
import { LayoutView, UnorderedListView } from "../../components";
import { ReadFromStorage, SaveToStorage } from "../../utils";
import { 
    ErrorResponse, 
    QrCodeRequest, 
    QrData, 
    QrCodeValidatorResponse,
    ValidateTokenRequest
} from "../../api";

const texts = [
    "zeskanuj kod QR w aplikacji mObywatel na telefonie", 
    "wyraź zgodę na przekazanie danych osobowych do mBanku"
];

export const QrCodePage = (): JSX.Element => {
    const params = useParams();
    const navigate = useNavigate();
    const state = useContext(Context);

    const [isLoading, setIsLoading] = useState(false);
    const [qrCode, setQrCode] = useState<QrData | undefined>(undefined);
    const [progress, setProgress] = useState<number>(qrCode?.lifetime ?? 0);

    const navigateToInactivePage = React.useCallback(() => navigate("/inactive"), []);
    const navigateToErrorPage = React.useCallback(() => navigate("/error"), []);

    const toInactivePage = React.useCallback((shouldNavigate: boolean) => {
        if (shouldNavigate) {
            navigateToInactivePage();
        }
    }, [ ]);

    const checkQrCodeTimeout = React.useCallback(() => {
        if (!qrCode) {
            return;
        }

        const secondsLeft = Math.round((qrCode.timeout.getTime() - Date.now()) / 1000);
        setProgress(secondsLeft > 0 ? secondsLeft : 0);
        toInactivePage(secondsLeft <= 0);
    },
    [ qrCode ]);

    const checkAlreadyUsedToken = React.useCallback(() => {
        ValidateTokenRequest(params.code ?? "", (response: QrCodeValidatorResponse) => {
            toInactivePage(!response.isActive);
        }, (fail: ErrorResponse) => {
            state.setAppErrorState({ type: fail.type });
            navigateToErrorPage();
        });
    },
    [ params.code ]);

    React.useEffect(() => {
        const savedQrCode = ReadFromStorage<QrData>(`qrcode-${params.code}`);
        if (savedQrCode) {
            savedQrCode.timeout = new Date(savedQrCode.timeout);
            setQrCode(savedQrCode);
            return;
        }

        setIsLoading(true);
        QrCodeRequest(params.code ?? "", (response) => {
            const qrData : QrData= {
                value: response.qrCode,
                timeout:  new Date(Date.now() + response.timeout * 1000),
                lifetime: response.lifetime
            }
            setQrCode(qrData);
            setIsLoading(false);
            SaveToStorage(`qrcode-${params.code}`, qrData);
        }, (fail: ErrorResponse) => {
            setIsLoading(false);

            if (!fail.type) {
                state.setAppErrorState({ type: "UnknownErrorType" });
                navigateToErrorPage();
                return;
            }

            if (["Citizen-QrCodeTimeoutError", "Citizen-QrCodeAlreadyRetrievedError"].indexOf(fail.type) >= 0) {
                navigateToInactivePage();
            }

            if (["Citizen-GeneralError", "InvalidToken"].indexOf(fail.type) >= 0) {
                state.setAppErrorState({ type: "InvalidToken" });
                navigateToErrorPage();
            }

            state.setAppErrorState({ type: fail.type });
            navigateToErrorPage();
        });
    }, 
    [ params.code ])

    React.useEffect(() => {
        if (params.code === undefined) {
            return;
        }

        if (qrCode?.timeout === undefined) {
            return;
        }

        const intervalId = window.setInterval(() => {
            checkQrCodeTimeout();
            checkAlreadyUsedToken();
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, 
    [
        params.code, 
        qrCode?.timeout
    ])

    return (
        <LayoutView>
            <Box>
                <Text as="h1" styledAs="h2">weryfikacja dokumentu</Text>
            </Box>
            <Box>
                <Text as="h4">informacje dla klienta:</Text>
            </Box>
            <Box>
                <UnorderedListView spacing={16} items={texts} />
            </Box>
            <Box>
                {isLoading 
                ? <Loader size={LoaderSize.EXTRA_EXTRA_LARGE} /> 
                : <></>}
                {qrCode 
                ? <> <QRCodeSVG 
                        value={qrCode?.value ?? ""}
                        size={256}
                        imageSettings={{
                            src: "mbank.svg",
                            x: undefined,
                            y: undefined,
                            height: 48,
                            width: 48,
                            excavate: true,
                        }}
                    />
                    <Box width={250} marginLeft={8}>
                        <Progress 
                            value={progress} 
                            max={qrCode?.lifetime} 
                            isCountdown={true}
                            isValueVisible={true}
                            color={ProgressColor.POSITIVE_CONTRAST}
                        />
                    </Box></> 
                : <></>}
            </Box>
        </LayoutView>
    );
}
